import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ExportButtons from '../components/ExportButtons';

const DOW = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const NTH = ['', '1st', '2nd', '3rd', '4th'];
const DELAY_REASONS = ['Emergency support request','Network outage','Classroom issue','Staff unavailable','Other'];
const todayStr = () => { const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; };

function scheduleSummary(d) {
  const t = (d.start_time || '').slice(0,5);
  if (d.frequency === 'daily')  return `Daily (Mon–Fri) · ${t}`;
  if (d.frequency === 'weekly') return `Weekly · ${DOW[d.weekday] || '?'} · ${t}`;
  if (d.frequency === 'custom') {
    const ds = d.custom_dates || [];
    return `On ${ds.length} date${ds.length===1?'':'s'}${ds.length?': '+ds.slice(0,3).join(', ')+(ds.length>3?'…':''):''} · ${t}`;
  }
  if (d.frequency === 'monthly') {
    if (d.monthly_mode === 'day')      return `Monthly · day ${d.monthly_day} · ${t}`;
    if (d.monthly_mode === 'last_dow') return `Monthly · last ${DOW[d.monthly_dow]} · ${t}`;
    if (d.monthly_mode === 'nth_dow')  return `Monthly · ${NTH[d.monthly_nth]} ${DOW[d.monthly_dow]} · ${t}`;
  }
  return d.frequency;
}
function displayStatus(t) {
  if (t.status === 'completed')   return { label:'Completed',   cls:'badge-completed' };
  if (t.status === 'in_progress') return { label:'In Progress', cls:'badge-discussed' };
  if (t.delay_status === 'pending')  return { label:'Delay Approval Pending', cls:'badge-pending' };
  if (t.delay_status === 'approved') return { label:'Delay Approved', cls:'badge-completed' };
  if (t.delay_status === 'rejected') return { label:'Delay Rejected', cls:'badge-faulty' };
  const overdue = new Date() > new Date(new Date(t.scheduled_at).getTime() + (t.grace_minutes||0)*60000);
  if (overdue) return { label:'Delayed', cls:'badge-faulty' };
  return { label:'Pending', cls:'badge-pending' };
}
const fileToData = file => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = e => res({ filename:file.name, mime_type:file.type, data:e.target.result });
  r.onerror = rej; r.readAsDataURL(file);
});

// ─── Complete modal (remarks + evidence) ─────────────────────────────────────
function CompleteModal({ task, onClose, onDone }) {
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const needEvidence = task.evidence_mode !== 'remarks_only';
  const minN = needEvidence ? Math.max(1, task.min_screenshots) : 0;

  const onPick = async e => {
    const picked = Array.from(e.target.files || []);
    const ok = picked.filter(f => /^image\/|application\/pdf/.test(f.type));
    const data = await Promise.all(ok.map(fileToData));
    setFiles(prev => task.evidence_mode === 'single' ? data.slice(0,1) : [...prev, ...data]);
  };
  const submit = async () => {
    if (!remarks.trim()) { setErr('Remarks are required'); return; }
    if (needEvidence && files.length < minN) { setErr(`Attach at least ${minN} file(s)`); return; }
    setBusy(true); setErr('');
    try { await api.post(`/routines/instances/${task.id}/complete`, { remarks:remarks.trim(), evidence:files }); onDone(); onClose(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:540 }}>
        <div className="modal-header"><span className="modal-title">Complete — {task.name}</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group"><label>Remarks *</label>
          <textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="e.g. Visited all classrooms. Smart panels and projectors checked. No issues found." /></div>
        {needEvidence && (
          <div className="form-group">
            <label>Evidence ({task.evidence_mode==='single'?'1 file':`min ${minN} files`}) — JPG / PNG / PDF</label>
            <input type="file" accept="image/*,application/pdf" multiple={task.evidence_mode==='multiple'} onChange={onPick}
              style={{ padding:'8px 0', background:'transparent', border:'none', color:'var(--text)' }} />
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
              {files.map((f,i) => (
                <div key={i} style={{ position:'relative' }}>
                  {/^image\//.test(f.mime_type)
                    ? <img src={f.data} alt="" style={{ width:60, height:60, objectFit:'cover', borderRadius:6, border:'1px solid var(--border)' }} />
                    : <div style={{ width:60, height:60, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:6, border:'1px solid var(--border)', fontSize:11 }}>PDF</div>}
                  <button onClick={() => setFiles(fs => fs.filter((_,j)=>j!==i))} style={{ position:'absolute', top:-6, right:-6, background:'var(--red)', color:'#fff', border:'none', borderRadius:'50%', width:18, height:18, cursor:'pointer', fontSize:11 }}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy?'Saving…':'Complete Task'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Delay reason modal ──────────────────────────────────────────────────────
function DelayModal({ task, onClose, onDone }) {
  const [reason, setReason] = useState(''); const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const submit = async () => {
    const r = reason === 'Other' ? other.trim() : reason;
    if (!r) { setErr('Select or enter a reason'); return; }
    setBusy(true); setErr('');
    try { await api.post(`/routines/instances/${task.id}/delay`, { reason:r }); onDone(); onClose(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:460 }}>
        <div className="modal-header"><span className="modal-title">Why was this task not started on time?</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group"><label>Delay Reason *</label>
          <select value={reason} onChange={e => setReason(e.target.value)}>
            <option value="">— Select —</option>
            {DELAY_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select></div>
        {reason === 'Other' && <div className="form-group"><input value={other} onChange={e => setOther(e.target.value)} placeholder="Describe the reason" /></div>}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy?'Submitting…':'Submit Reason'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Today's Tasks (IT staff) ────────────────────────────────────────────────
function TodayTasks({ campuses }) {
  const [date, setDate] = useState(todayStr());
  const [campus, setCampus] = useState('');
  const [rows, setRows] = useState([]);
  const [completeFor, setCompleteFor] = useState(null);
  const [delayFor, setDelayFor] = useState(null);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(() => {
    const params = { date };
    if (campus) params.campus_id = campus;
    api.get('/routines/my', { params }).then(r => setRows(r.data)).catch(()=>{});
  }, [date, campus]);
  useEffect(() => { load(); }, [load]);

  const start = async t => { await api.post(`/routines/instances/${t.id}/start`); load(); };

  const groups = { Pending:[], Delayed:[], 'In Progress':[], Completed:[] };
  rows.forEach(t => {
    const d = displayStatus(t);
    if (d.label === 'Completed') groups.Completed.push(t);
    else if (d.label === 'In Progress') groups['In Progress'].push(t);
    else if (d.label === 'Pending') groups.Pending.push(t);
    else groups.Delayed.push(t);   // Delayed / Delay Approval Pending / Approved / Rejected
  });

  const Card = ({ t }) => {
    const d = displayStatus(t);
    const notStarted = t.status === 'pending';
    return (
      <div className="card" style={{ marginBottom:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontWeight:600 }}>{t.name} <span className={`badge ${d.cls}`} style={{ marginLeft:6 }}>{d.label}</span></div>
            <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>
              {t.campus_name} · {t.frequency} · scheduled {new Date(t.scheduled_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
              {t.evidence_mode !== 'remarks_only' && ` · evidence: ${t.evidence_mode==='single'?'1':'min '+t.min_screenshots}`}
            </div>
            {t.delay_reason && <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>Delay: {t.delay_reason}{t.delay_status==='rejected' && t.delay_admin_remarks ? ` · ✗ ${t.delay_admin_remarks}` : ''}</div>}
          </div>
          <div className="flex-gap" style={{ gap:6 }}>
            {notStarted && d.label==='Delayed' && <button className="btn btn-secondary btn-sm" onClick={() => setDelayFor(t)}>Delay Reason</button>}
            {notStarted && <button className="btn btn-primary btn-sm" onClick={() => start(t)}>Start Task</button>}
            {t.status==='in_progress' && <button className="btn btn-primary btn-sm" onClick={() => setCompleteFor(t)}>Complete Task</button>}
            {t.status==='completed' && <button className="btn btn-secondary btn-sm" onClick={() => setViewer(t.id)}>View</button>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex-gap" style={{ gap:10, marginBottom:16 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding:'6px 8px' }} />
        {campuses.length > 1 && (
          <select value={campus} onChange={e => setCampus(e.target.value)} style={{ padding:'6px 8px' }}>
            <option value="">All my campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>
      {!rows.length && <div className="card"><div className="empty-state"><p>No tasks scheduled for this day.</p></div></div>}
      {['Pending','Delayed','In Progress','Completed'].map(g => groups[g].length > 0 && (
        <div key={g} style={{ marginBottom:18 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>{g} ({groups[g].length})</div>
          {groups[g].map(t => <Card key={t.id} t={t} />)}
        </div>
      ))}
      {completeFor && <CompleteModal task={completeFor} onClose={() => setCompleteFor(null)} onDone={load} />}
      {delayFor && <DelayModal task={delayFor} onClose={() => setDelayFor(null)} onDone={load} />}
      {viewer && <InstanceViewer id={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function InstanceViewer({ id, onClose }) {
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/routines/instances/${id}`).then(r => setT(r.data)).catch(()=>{}); }, [id]);
  if (!t) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header"><span className="modal-title">{t.name}</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div style={{ fontSize:13, marginBottom:8 }}>{t.campus_name} · {new Date(t.scheduled_at).toLocaleString()}</div>
        <div style={{ fontSize:12, color:'var(--text2)' }}>Completed by {t.completed_by_name} at {t.completed_at ? new Date(t.completed_at).toLocaleString() : '—'}{t.on_time===false ? ' · (late)' : t.on_time ? ' · on time' : ''}</div>
        <div style={{ margin:'10px 0' }}><b style={{ fontSize:12 }}>Remarks</b><div style={{ fontSize:13 }}>{t.remarks || '—'}</div></div>
        {(t.evidence||[]).length > 0 && (
          <div><b style={{ fontSize:12 }}>Evidence</b>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
              {t.evidence.map(e => /^image\//.test(e.mime_type)
                ? <a key={e.id} href={e.data} download={e.filename}><img src={e.data} alt="" style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:'1px solid var(--border)' }} /></a>
                : <a key={e.id} href={e.data} download={e.filename} className="btn btn-secondary btn-sm">⬇ {e.filename}</a>)}
            </div>
          </div>
        )}
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ─── Delay Approvals (admin) ─────────────────────────────────────────────────
function DelayApprovals() {
  const [rows, setRows] = useState([]);
  const [reject, setReject] = useState(null); const [remarks, setRemarks] = useState('');
  const load = useCallback(() => { api.get('/routines/delays').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); }, [load]);
  const review = async (id, decision, rmk) => { await api.patch(`/routines/instances/${id}/delay-review`, { decision, remarks:rmk }); load(); };
  return (
    <div>
      {!rows.length ? <div className="card"><div className="empty-state"><p>No delays awaiting review.</p></div></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}><div className="table-wrap"><table>
          <thead><tr><th>Task</th><th>Campus</th><th>Date</th><th>Reason</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize:13 }}>{r.name}</td>
                <td style={{ fontSize:13 }}>{r.campus_name}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{new Date(r.scheduled_date).toLocaleDateString()}</td>
                <td style={{ fontSize:13 }}>{r.delay_reason}</td>
                <td><span className={`badge ${r.delay_status==='pending'?'badge-pending':'badge-faulty'}`}>{r.delay_status}</span></td>
                <td>{r.delay_status==='pending' && (
                  <div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => review(r.id,'approved')}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => { setReject(r); setRemarks(''); }}>Reject</button>
                  </div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div></div>
      )}
      {reject && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setReject(null)}>
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header"><span className="modal-title">Reject Delay — {reject.name}</span><button className="modal-close" onClick={() => setReject(null)}>×</button></div>
            <div className="form-group"><label>Admin Remarks * (mandatory)</label><textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReject(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { if(remarks.trim()){ review(reject.id,'rejected',remarks.trim()); setReject(null);} }}>Reject + Escalate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Compliance (admin) ──────────────────────────────────────────────────────
function Compliance({ campuses }) {
  const [dash, setDash] = useState(null);
  const [filters, setFilters] = useState({ frequency:'', campus_id:'', from:'', to:'' });
  const [report, setReport] = useState(null);
  useEffect(() => { api.get('/routines/dashboard').then(r => setDash(r.data)).catch(()=>{}); }, []);
  const runReport = useCallback(() => {
    const params = {}; Object.entries(filters).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/routines/report', { params }).then(r => setReport(r.data)).catch(()=>{});
  }, [filters]);
  useEffect(() => { runReport(); }, [runReport]);

  const Cards = ({ title, s }) => (
    <div className="card" style={{ flex:1, minWidth:200 }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>{title}</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 16px', fontSize:13 }}>
        <span>Total <b>{s.total}</b></span>
        <span style={{ color:'var(--green)' }}>Completed <b>{s.completed}</b></span>
        <span style={{ color:'var(--amber)' }}>Pending <b>{s.pending}</b></span>
        <span style={{ color:'var(--red)' }}>Overdue <b>{s.overdue}</b></span>
        {title!=='Daily' && <span>Evidence <b>{s.with_evidence}</b></span>}
      </div>
    </div>
  );

  return (
    <div>
      {dash && (
        <div className="flex-gap" style={{ gap:12, marginBottom:20, flexWrap:'wrap' }}>
          <Cards title="Daily" s={dash.daily} />
          <Cards title="Weekly" s={dash.weekly} />
          <Cards title="Monthly" s={dash.monthly} />
        </div>
      )}
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:600 }}>Compliance Report</div>
        {report && <ExportButtons filename="routines-compliance" title="Routines Compliance Report" rows={report.rows}
          columns={[{key:'scheduled_date',label:'Date'},{key:'name',label:'Task'},{key:'frequency',label:'Frequency'},{key:'campus_name',label:'Campus'},{key:'status',label:'Status'},{key:'on_time',label:'On Time'},{key:'delay_status',label:'Delay'},{key:'completed_by_name',label:'Completed By'},{key:'remarks',label:'Remarks'}]} />}
      </div>
      <div className="flex-gap" style={{ gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <select value={filters.frequency} onChange={e => setFilters(f=>({...f,frequency:e.target.value}))} style={{ padding:'6px 8px' }}>
          <option value="">All frequencies</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
        </select>
        <select value={filters.campus_id} onChange={e => setFilters(f=>({...f,campus_id:e.target.value}))} style={{ padding:'6px 8px' }}>
          <option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="date" value={filters.from} onChange={e => setFilters(f=>({...f,from:e.target.value}))} style={{ padding:'6px 8px' }} title="From" />
        <input type="date" value={filters.to} onChange={e => setFilters(f=>({...f,to:e.target.value}))} style={{ padding:'6px 8px' }} title="To" />
      </div>
      {report && (
        <>
          <div className="flex-gap" style={{ gap:16, marginBottom:12, fontSize:13, flexWrap:'wrap' }}>
            <span>Total <b>{report.metrics.total}</b></span>
            <span>On-time <b>{report.metrics.on_time}</b></span>
            <span>Approved delays <b>{report.metrics.approved_delays}</b></span>
            <span>Rejected <b>{report.metrics.rejected_delays}</b></span>
            <span style={{ color:'var(--accent)' }}>Compliance <b>{report.metrics.compliance}%</b></span>
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden' }}><div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Task</th><th>Campus</th><th>Status</th><th>On time</th><th>Delay</th><th>By</th></tr></thead>
            <tbody>
              {report.rows.map(r => { const d = displayStatus(r); return (
                <tr key={r.id}>
                  <td style={{ fontSize:12 }}>{new Date(r.scheduled_date).toLocaleDateString()}</td>
                  <td style={{ fontSize:13 }}>{r.name}</td>
                  <td style={{ fontSize:13 }}>{r.campus_name}</td>
                  <td><span className={`badge ${d.cls}`}>{d.label}</span></td>
                  <td style={{ fontSize:12 }}>{r.on_time===true?'✓':r.on_time===false?'✗':'—'}</td>
                  <td style={{ fontSize:12 }}>{r.delay_status!=='none'?r.delay_status:'—'}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{r.completed_by_name || r.started_by_name || '—'}</td>
                </tr>
              );})}
            </tbody>
          </table></div></div>
        </>
      )}
    </div>
  );
}

// ─── Definitions (admin) ─────────────────────────────────────────────────────
const BLANK = { name:'', description:'', frequency:'daily', start_time:'09:00', weekday:6,
  monthly_mode:'day', monthly_day:1, monthly_dow:6, monthly_nth:1, grace_minutes:15,
  evidence_mode:'remarks_only', min_screenshots:0, all_campuses:true, campus_ids:[], custom_dates:[] };

function Definitions({ campuses }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get('/routines/defs').then(r => setRows(r.data)).catch(()=>{}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setModal({ ...BLANK });
  const openEdit = async id => { const r = await api.get(`/routines/defs/${id}`); setModal({ ...BLANK, ...r.data, start_time:(r.data.start_time||'09:00').slice(0,5) }); };
  const save = async () => {
    if (!modal.name.trim()) return;
    setBusy(true);
    try {
      const body = { ...modal };
      if (modal.id) await api.put(`/routines/defs/${modal.id}`, body);
      else await api.post('/routines/defs', body);
      setModal(null); load();
    } finally { setBusy(false); }
  };
  const toggle = async (d, field) => { await api.patch(`/routines/defs/${d.id}`, { [field]: field==='active'?!d.active:true }); load(); };

  const set = (k,v) => setModal(m => ({ ...m, [k]:v }));

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:14 }}>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Define recurring tasks; the system auto-generates them at the scheduled time and assigns to the campus IT staff.</p>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Create Task</button>
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No tasks defined yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Task</th><th>Schedule</th><th>Evidence</th><th>Campuses</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.id}>
                  <td><div style={{ fontWeight:500 }}>{d.name}</div><div style={{ fontSize:11, color:'var(--text2)' }}>{d.description}</div></td>
                  <td style={{ fontSize:12 }}>{scheduleSummary(d)} <span style={{ color:'var(--text3)' }}>· grace {d.grace_minutes}m</span></td>
                  <td style={{ fontSize:12 }}>{d.evidence_mode==='remarks_only'?'Remarks only':d.evidence_mode==='single'?'1 file':`min ${d.min_screenshots}`}</td>
                  <td style={{ fontSize:12 }}>{d.all_campuses?'All':d.campus_count}</td>
                  <td style={{ fontSize:12 }}>{d.active?'✓':'—'}</td>
                  <td><div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(d.id)}>Edit</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => toggle(d,'active')}>{d.active?'Disable':'Enable'}</button>
                    <button className="btn btn-danger btn-sm" onClick={() => { if(window.confirm('Archive this task?')) toggle(d,'archived'); }}>Archive</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'Create'} Task</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Task Name *</label><input value={modal.name} onChange={e => set('name',e.target.value)} /></div>
            <div className="form-group"><label>Description</label><textarea rows={2} value={modal.description||''} onChange={e => set('description',e.target.value)} /></div>
            <div className="form-row">
              <div className="form-group"><label>Frequency</label>
                <select value={modal.frequency} onChange={e => set('frequency',e.target.value)}>
                  <option value="daily">Daily (Mon–Fri)</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="custom">Miscellaneous (specific dates)</option>
                </select></div>
              <div className="form-group"><label>Start Time</label><input type="time" value={modal.start_time} onChange={e => set('start_time',e.target.value)} /></div>
              <div className="form-group"><label>Grace (min)</label><input type="number" value={modal.grace_minutes} onChange={e => set('grace_minutes',parseInt(e.target.value)||0)} /></div>
            </div>
            {modal.frequency==='daily' && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:8 }}>Daily tasks run automatically Monday to Friday.</div>}
            {modal.frequency==='weekly' && (
              <div className="form-group"><label>Day of week</label>
                <select value={modal.weekday} onChange={e => set('weekday',parseInt(e.target.value))}>
                  {DOW.map((d,i) => <option key={i} value={i}>{d}</option>)}
                </select></div>
            )}
            {modal.frequency==='custom' && (
              <div className="form-group"><label>Dates</label>
                <div className="flex-gap" style={{ gap:8, marginBottom:8 }}>
                  <input type="date" id="rt-add-date" style={{ padding:'6px 8px' }} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                    const el = document.getElementById('rt-add-date'); const v = el && el.value;
                    if (v && !(modal.custom_dates||[]).includes(v)) set('custom_dates', [...(modal.custom_dates||[]), v].sort());
                  }}>+ Add date</button>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {(modal.custom_dates||[]).map(d => (
                    <span key={d} className="badge badge-pending" style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                      {d}<button type="button" onClick={() => set('custom_dates', modal.custom_dates.filter(x=>x!==d))} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer' }}>×</button>
                    </span>
                  ))}
                  {!(modal.custom_dates||[]).length && <span style={{ fontSize:12, color:'var(--text2)' }}>Add one or more dates this task should run on.</span>}
                </div>
              </div>
            )}
            {modal.frequency==='monthly' && (
              <div className="form-row">
                <div className="form-group"><label>Monthly rule</label>
                  <select value={modal.monthly_mode} onChange={e => set('monthly_mode',e.target.value)}>
                    <option value="day">Day of month</option><option value="last_dow">Last weekday</option><option value="nth_dow">Nth weekday</option>
                  </select></div>
                {modal.monthly_mode==='day' && <div className="form-group"><label>Day (1–28)</label><input type="number" min="1" max="28" value={modal.monthly_day} onChange={e => set('monthly_day',parseInt(e.target.value)||1)} /></div>}
                {modal.monthly_mode!=='day' && <div className="form-group"><label>Weekday</label><select value={modal.monthly_dow} onChange={e => set('monthly_dow',parseInt(e.target.value))}>{DOW.map((d,i)=><option key={i} value={i}>{d}</option>)}</select></div>}
                {modal.monthly_mode==='nth_dow' && <div className="form-group"><label>Which</label><select value={modal.monthly_nth} onChange={e => set('monthly_nth',parseInt(e.target.value))}>{[1,2,3,4].map(n=><option key={n} value={n}>{NTH[n]}</option>)}</select></div>}
              </div>
            )}
            <div className="form-row">
              <div className="form-group"><label>Evidence</label>
                <select value={modal.evidence_mode} onChange={e => set('evidence_mode',e.target.value)}>
                  <option value="remarks_only">Remarks only</option><option value="single">Remarks + 1 screenshot</option><option value="multiple">Remarks + multiple screenshots</option>
                </select></div>
              {modal.evidence_mode==='multiple' && <div className="form-group"><label>Min screenshots</label><input type="number" min="1" value={modal.min_screenshots} onChange={e => set('min_screenshots',parseInt(e.target.value)||1)} /></div>}
            </div>
            <div className="form-group">
              <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, textTransform:'none', letterSpacing:0 }}>
                <input type="checkbox" checked={modal.all_campuses} onChange={e => set('all_campuses',e.target.checked)} style={{ width:15, height:15 }} /> All campuses
              </label>
            </div>
            {!modal.all_campuses && (
              <div className="form-group"><label>Campuses</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:140, overflowY:'auto' }}>
                  {campuses.map(c => {
                    const on = (modal.campus_ids||[]).includes(c.id);
                    return <button key={c.id} type="button" className={`btn btn-sm ${on?'btn-primary':'btn-secondary'}`}
                      onClick={() => set('campus_ids', on ? modal.campus_ids.filter(x=>x!==c.id) : [...(modal.campus_ids||[]), c.id])}>{c.name}</button>;
                  })}
                </div>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !modal.name.trim()}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────
// ── My Timings ──────────────────────────────────────────────────────────────
// Set the time each routine actually runs at your campus. The scheduled time
// stays visible beside it, so a large gap is obvious to whoever set it.
function MyTimings({ campuses }) {
  const { user, isAdmin } = useAuth();
  const [campus, setCampus] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!campus && campuses.length) setCampus(String(campuses[0].id));
  }, [campuses, campus]);

  const load = useCallback(() => {
    if (!campus) return;
    api.get('/routines/times', { params:{ campus_id: campus } })
      .then(r => setRows(r.data)).catch(() => setRows([]));
  }, [campus]);
  useEffect(() => { load(); }, [load]);

  const flash = t => { setMsg(t); setTimeout(() => setMsg(''), 2500); };
  const save = async (row, value) => {
    setBusy(row.def_id);
    try {
      await api.put(`/routines/times/${row.def_id}`, { campus_id: Number(campus), start_time: value });
      flash(value ? `✓ ${row.name} set to ${value}` : `✓ ${row.name} back to the scheduled time`);
      load();
    } catch (e) { flash(e.response?.data?.error || 'Could not save the time'); }
    finally { setBusy(null); }
  };

  if (!campuses.length) return <div className="card"><div className="empty-state"><p>No campus assigned to you.</p></div></div>;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:10 }}>
        <p style={{ fontSize:12.5, color:'var(--text2)', maxWidth:620 }}>
          Set when each routine runs at your campus. The task appears at your time
          instead of the scheduled one, so starting early counts as on time.
          Leave it blank to follow the scheduled time.
        </p>
        {(campuses.length > 1 || isAdmin) && (
          <select value={campus} onChange={e => setCampus(e.target.value)} style={{ width:200 }}>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>
      {msg && <div style={{ fontSize:12.5, color:'var(--accent)', marginBottom:10 }}>{msg}</div>}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap"><table>
          <thead><tr>
            <th>Routine</th><th>Frequency</th>
            <th style={{ textAlign:'center' }}>Scheduled</th>
            <th style={{ textAlign:'center' }}>Your time</th>
            <th>Set by</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const moved = r.campus_time && r.campus_time !== r.default_time;
              return (
                <tr key={r.def_id}>
                  <td>
                    <div style={{ fontWeight:600 }}>{r.name}</div>
                    {r.description && <div style={{ fontSize:11.5, color:'var(--text2)' }}>{r.description}</div>}
                  </td>
                  <td style={{ textTransform:'capitalize', color:'var(--text2)' }}>{r.frequency}</td>
                  <td style={{ textAlign:'center', color:'var(--text2)' }}>{r.default_time}</td>
                  <td style={{ textAlign:'center' }}>
                    <div className="flex-gap" style={{ gap:6, justifyContent:'center', alignItems:'center' }}>
                      <input type="time" defaultValue={r.campus_time || ''} disabled={busy === r.def_id}
                        onBlur={e => { const v = e.target.value; if (v !== (r.campus_time || '')) save(r, v); }}
                        style={{ width:120 }} />
                      {moved && (
                        <button className="btn btn-secondary btn-sm" title="Follow the scheduled time again"
                          onClick={() => save(r, '')}>↺</button>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize:11.5, color:'var(--text2)' }}>
                    {r.campus_time ? (r.set_by_name || '—') : <span style={{ opacity:.6 }}>using the scheduled time</span>}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={5}><div className="empty-state"><p>No routines apply to this campus.</p></div></td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

export default function Routines() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('today');
  const [campuses, setCampuses] = useState([]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active && (isAdmin || (user.campus_ids||[]).includes(c.id))))).catch(()=>{});
  }, [isAdmin, user]);

  const canManage = isAdmin || user.role === 'it_head';
  // "My Timings" is for the person doing the work, so it is not gated behind
  // the management tabs.
  const tabs = [['today',"Today's Tasks"], ['times','My Timings']];
  if (canManage) tabs.push(['defs','Task Definitions'], ['delays','Delay Approvals'], ['compliance','Compliance']);

  return (
    <div>
      <div style={{ marginBottom:18 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Routines</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Daily, weekly & monthly IT operations — tracked, auditable and compliance-driven.</p>
      </div>
      <div style={{ display:'flex', gap:0, marginBottom:18, borderBottom:'1px solid var(--border)' }}>
        {tabs.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'9px 18px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
              borderBottom: tab===id?'2px solid var(--accent)':'2px solid transparent',
              color: tab===id?'var(--accent)':'var(--text2)', fontWeight: tab===id?600:400, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>
      {tab==='today'      && <TodayTasks campuses={campuses} />}
      {tab==='times'      && <MyTimings campuses={campuses} />}
      {tab==='defs'       && canManage && <Definitions campuses={campuses} />}
      {tab==='delays'     && canManage && <DelayApprovals />}
      {tab==='compliance' && canManage && <Compliance campuses={campuses} />}
    </div>
  );
}
